import React, { useState, useEffect } from 'react';
import type { Settings } from '../../types';
import { useSEO } from '../../hooks/useSEO';

interface ContactUsPageProps {
    settings: Settings;
}

const ContactUsPage: React.FC<ContactUsPageProps> = ({ settings }) => {
    const [formData, setFormData] = useState({ name: '', email: '', topic: 'General Inquiry', message: '' });
    const [submitted, setSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');

    useSEO({
        title: `Contact Us | ${settings?.companyName || 'ARCH.'}`,
        description: `Get in touch with ${settings?.companyName || 'ARCH.'} to discuss your design project, request a consultation, or locate our office.`,
        ogImage: settings?.openGraph?.imageUrl
    }, [settings]);

     useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setSubmitError('');

        const useResend = settings.emailService === 'resend';

        if (!useResend && !settings.formspreeEndpoint) {
            console.error("Contact form endpoint is not configured in settings.");
            setSubmitError("The contact form is not configured. Please contact the site administrator.");
            return;
        }

        setIsSubmitting(true);
        try {
            let response;
            if (useResend) {
                 response = await fetch('/api/send-email', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        name: formData.name,
                        email: formData.email,
                        topic: formData.topic,
                        message: formData.message,
                        toEmail: settings.email,
                        senderEmail: settings.resendSenderEmail
                    })
                });
            } else {
                response = await fetch(settings.formspreeEndpoint!, {
                    method: 'POST',
                    body: new FormData(e.target as HTMLFormElement),
                    headers: {
                        'Accept': 'application/json'
                    }
                });
            }

            if (response.ok) {
                setSubmitted(true);
                setFormData({ name: '', email: '', topic: 'General Inquiry', message: '' });
            } else {
                let errorMessage = "An unexpected error occurred. Please try again.";
                try {
                    const data = await response.json();
                    if (useResend) {
                        errorMessage = data.error || errorMessage;
                    } else if (data.errors) {
                        errorMessage = data.errors.map((error: any) => error.message).join(', ');
                    }
                } catch (parseError) {
                    if (useResend && window.location.hostname === 'localhost') {
                        errorMessage = "Local serverless functions are not running. Run 'npx vercel dev' to submit contact forms locally.";
                    }
                }
                setSubmitError(errorMessage);
            }
        } catch (error) {
            setSubmitError("There was a problem sending your message. Please check your network connection.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="bg-brand-light text-brand-dark pt-24">
            <div className="container mx-auto px-6 py-16 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                {/* Left Side: Info & Form */}
                <div className="py-8">
                    <h1 className="text-5xl md:text-6xl font-extrabold tracking-tight font-heading">Contact Us</h1>
                    <p className="mt-4 text-lg text-gray-600 font-body">Have a project in mind? We'd love to hear about it. Reach out to us to discuss how we can bring your vision to life.</p>
                    
                    <div className="mt-8 pt-8 border-t border-gray-200 space-y-4 text-gray-700">
                        <p><strong>Address:</strong> {settings.address}</p>
                        <p><strong>Email:</strong> <a href={`mailto:${settings.email}`} className="hover:underline">{settings.email}</a></p>
                        <p><strong>Phone:</strong> {settings.phone}</p>
                    </div>

                    <div className="mt-8">
                         {submitted ? (
                            <div className="bg-gray-100 border-l-4 border-gray-800 text-brand-dark p-8 text-center">
                                <h2 className="text-2xl font-bold mb-2 font-heading">Thank You!</h2>
                                <p>Your message has been sent. We will get back to you shortly.</p>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="w-full space-y-6">
                                {/* Honeypot field for spam prevention with Formspree */}
                                <div style={{ position: 'absolute', left: '-5000px' }} aria-hidden="true">
                                    <input type="text" name="_gotcha" tabIndex={-1} autoComplete="off" />
                                </div>
                                <div>
                                    <label htmlFor="name" className="sr-only">Name</label>
                                    <input type="text" name="name" id="name" placeholder="Your Name" required value={formData.name} onChange={handleChange} className="w-full p-4 bg-gray-100 border border-gray-300 focus:ring-2 focus:ring-brand-dark focus:outline-none transition-all" />
                                </div>
                                <div>
                                    <label htmlFor="email" className="sr-only">Email</label>
                                    <input type="email" name="email" id="email" placeholder="Your Email" required value={formData.email} onChange={handleChange} className="w-full p-4 bg-gray-100 border border-gray-300 focus:ring-2 focus:ring-brand-dark focus:outline-none transition-all" />
                                </div>
                                <div>
                                    <label htmlFor="topic" className="sr-only">Topic of Inquiry</label>
                                    <select 
                                        name="topic" 
                                        id="topic" 
                                        required 
                                        value={formData.topic} 
                                        onChange={handleChange} 
                                        className="w-full p-4 bg-gray-100 border border-gray-300 focus:ring-2 focus:ring-brand-dark focus:outline-none transition-all text-gray-700 appearance-none bg-no-repeat bg-[right_1rem_center]"
                                        style={{ backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%234b5563' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'></polyline></svg>")`, paddingRight: '2.5rem' }}
                                    >
                                        <option value="General Inquiry">General Inquiry</option>
                                        <option value="Architectural Services">Architectural Services</option>
                                        <option value="Design">Design</option>
                                        <option value="Masterplanning">Masterplanning</option>
                                        <option value="Tender & Contract">Tender & Contract</option>
                                        <option value="Interior">Interior</option>
                                        <option value="Landscape Design">Landscape Design</option>
                                        <option value="Project Management & Administration">Project Management & Administration</option>
                                        <option value="BIM">BIM</option>
                                        <option value="Others">Others</option>
                                    </select>
                                </div>
                                <div>
                                    <label htmlFor="message" className="sr-only">Message</label>
                                    <textarea name="message" id="message" rows={5} placeholder="Your Message" required value={formData.message} onChange={handleChange} className="w-full p-4 bg-gray-100 border border-gray-300 focus:ring-2 focus:ring-brand-dark focus:outline-none transition-all"></textarea>
                                </div>
                                <button type="submit" disabled={isSubmitting} className="w-full py-4 text-center bg-brand-dark text-white font-bold uppercase tracking-widest hover:bg-gray-700 transition-colors font-header disabled:bg-gray-500">
                                    {isSubmitting ? 'Sending...' : 'Send Message'}
                                </button>
                                {submitError && <p className="text-red-500 text-sm text-center">{submitError}</p>}
                            </form>
                        )}
                    </div>
                </div>

                {/* Right Side: Map */}
                <div className="h-[70vh] w-full bg-gray-200">
                    {settings.googleMapsUrl ? (
                        <iframe
                            src={settings.googleMapsUrl}
                            width="100%"
                            height="100%"
                            style={{ border: 0 }}
                            allowFullScreen={false}
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            className="grayscale"
                        ></iframe>
                    ) : (
                        <div className="w-full h-full bg-gray-200 flex items-center justify-center text-gray-500">
                            <p>Google Maps URL not configured.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ContactUsPage;